import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, Edit, Package, Plus, ChevronLeft, ChevronRight, Calendar, DollarSign, Wallet, FileText, CheckCircle2, IndianRupee, Loader2 } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import AppointmentWarning from "@/components/AppointmentWarning";
import { isDelhiveryAppointmentBooked } from "@/utils/appointmentUtils";

const STATUS_COLORS = {
  CREATED: "bg-blue-100 text-blue-800",
  MANIFESTED: "bg-purple-100 text-purple-800",
  PICKED_UP: "bg-yellow-100 text-yellow-800",
  LEFT_ORIGIN: "bg-orange-100 text-orange-800",
  REACH_DESTINATION: "bg-cyan-100 text-cyan-800",
  UNDEL_REATTEMPT: "bg-amber-100 text-amber-800",
  PART_DEL: "bg-lime-100 text-lime-800",
  OFD: "bg-teal-100 text-teal-800",
  DELIVERED: "bg-green-100 text-green-800",
};

const GRN_STATUS_COLORS = {
  Pending: "bg-yellow-100 text-yellow-800",
  Partial: "bg-blue-100 text-blue-800",
  Complete: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

const GRN_STATUSES = ["Pending", "Partial", "Complete", "Rejected"];
const PAYMENT_METHODS = ["Cash", "Card", "UPI", "Net Banking", "Cheque", "Others"];

const ITEMS_PER_PAGE = 50;

// The 8 Appointments tabs (must match backend APPOINTMENT_TABS keys).
const APPOINTMENT_TABS = [
  { key: 'breached', label: 'Breached' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'pending', label: 'Pending' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'returned', label: 'Returned' },
  { key: 'lost', label: 'Lost' },
  { key: 'cancelled', label: 'Cancelled' },
];
// Active (to-be-delivered) tabs get the schedule/edit-appointment actions; the
// terminal tabs get the GRN/payment columns.
const ACTIVE_TAB_KEYS = ['breached', 'today', 'tomorrow', 'pending'];

const SellerAppointments = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('breached');
  const [tabCounts, setTabCounts] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  // Summary metrics
  const [summary, setSummary] = useState({
    total_pending: 0,
    today_pending: 0,
    appointment_breached: 0,
    total_grn_pending: 0,
    pending_collection_amount: 0
  });
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarketplace, setSelectedMarketplace] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [marketplaces, setMarketplaces] = useState([]);
  
  // Dialog states
  const [showGRNDialog, setShowGRNDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaymentsListDialog, setShowPaymentsListDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // GRN form states
  const [grnStatus, setGrnStatus] = useState("Pending");
  const [dispatchQty, setDispatchQty] = useState(0);
  const [acceptedQty, setAcceptedQty] = useState(0);
  const [rejectedQty, setRejectedQty] = useState(0);
  const [grnRemarks, setGrnRemarks] = useState("");
  
  // Payment form states
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [transactionRef, setTransactionRef] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  
  // Payments list
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsRemaining, setPaymentsRemaining] = useState(0);

  useEffect(() => {
    fetchSummary();
    fetchMarketplaces();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [activeTab, currentPage, searchQuery, selectedMarketplace, selectedStatus, startDate, endDate]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/appointments/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const fetchTabCounts = async () => {
    try {
      const response = await axios.get(`${API}/appointments/tab-counts`);
      setTabCounts(response.data);
    } catch (error) {
      console.error("Failed to fetch tab counts:", error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tab: activeTab,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      if (searchQuery.trim()) params.append('search', searchQuery);

      const response = await axios.get(`${API}/appointments/list?${params.toString()}`);
      setOrders(response.data.orders);
      setTotalPages(response.data.pages);
      setTotalOrders(response.data.total);
      // Keep the tab badges in sync with any list refresh (incl. after mutations).
      fetchTabCounts();
    } catch (error) {
      toast.error("Failed to fetch appointments");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplaces = async () => {
    try {
      const response = await axios.get(`${API}/b2b-marketplace/`);
      setMarketplaces(response.data);
    } catch (error) {
      console.error("Failed to fetch marketplaces");
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedMarketplace("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    handleClearFilters();
  };

  // Active (to-be-delivered) tabs show schedule/edit-appointment actions; terminal
  // tabs (delivered/returned/lost/cancelled) show the GRN/payment columns.
  const isActiveTab = ACTIVE_TAB_KEYS.includes(activeTab);

  const handleOpenGRNDialog = async (order) => {
    setSelectedOrder(order);
    
    // Fetch existing GRN if any
    try {
      const response = await axios.get(`${API}/appointments/grn/${order.id}`);
      if (response.data.grn) {
        const grn = response.data.grn;
        setGrnStatus(grn.grn_status || "Pending");
        setDispatchQty(grn.total_dispatch_quantity || 0);
        setAcceptedQty(grn.total_accepted_quantity || 0);
        setRejectedQty(grn.total_rejected_quantity || 0);
        setGrnRemarks(grn.remarks || "");
      } else {
        // Reset form for new GRN
        setGrnStatus("Pending");
        setDispatchQty(0);
        setAcceptedQty(0);
        setRejectedQty(0);
        setGrnRemarks("");
      }
    } catch (error) {
      console.error("Failed to fetch GRN:", error);
    }
    
    setShowGRNDialog(true);
  };

  const handleSaveGRN = async () => {
    try {
      const grnData = {
        order_id: selectedOrder.id,
        grn_status: grnStatus,
        total_dispatch_quantity: parseInt(dispatchQty),
        total_accepted_quantity: parseInt(acceptedQty),
        total_rejected_quantity: parseInt(rejectedQty),
        remarks: grnRemarks
      };
      
      await axios.post(`${API}/appointments/grn`, grnData);
      toast.success("GRN updated successfully");
      setShowGRNDialog(false);
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update GRN");
    }
  };

  const handleOpenPaymentDialog = (order) => {
    setSelectedOrder(order);
    // Set default payment date to today
    const today = new Date().toISOString().split('T')[0];
    setPaymentDate(today);
    setPaymentMethod("Cash");
    setPaymentAmount("");
    setTransactionRef("");
    setPaymentRemarks("");
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    try {
      const paymentData = {
        order_id: selectedOrder.id,
        payment_method: paymentMethod,
        amount_collected: parseFloat(paymentAmount),
        payment_date: new Date(paymentDate).toISOString(),
        transaction_reference: transactionRef || null,
        remarks: paymentRemarks || null
      };
      
      const response = await axios.post(`${API}/appointments/payment`, paymentData);
      toast.success(`Payment recorded: ₹${paymentAmount}. Remaining: ₹${response.data.remaining}`);
      setShowPaymentDialog(false);
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleViewPayments = async (order) => {
    setSelectedOrder(order);
    try {
      const response = await axios.get(`${API}/appointments/payments/${order.id}`);
      setPaymentsList(response.data.payments);
      setPaymentsTotal(response.data.total_collected);
      setPaymentsRemaining(response.data.remaining);
      setShowPaymentsListDialog(true);
    } catch (error) {
      toast.error("Failed to fetch payment history");
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Appointments</h1>
          <p className="text-slate-600 mt-1">Manage delivery appointments and collections</p>
        </div>

        <div className="mb-6">
          <AppointmentWarning />
        </div>

        {/* Summary Cards — Breached / Today / Pending totals now live in the tabs below,
            so only the non-tab metrics (GRN + Collection) are surfaced here. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">GRN Pending</p>
                  <p className="text-xl font-bold text-slate-800">{summary.total_grn_pending}</p>
                </div>
                <FileText className="w-6 h-6 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Collection Pending</p>
                  <p className="text-xl font-bold text-slate-800">₹{summary.pending_collection_amount.toLocaleString()}</p>
                </div>
                <IndianRupee className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-x-1 gap-y-0 mb-4 border-b border-slate-200">
          {APPOINTMENT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-3 py-2.5 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === t.key
                  ? 'border-b-2 border-teal-600 text-teal-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {t.label} ({tabCounts[t.key] ?? 0})
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Search</Label>
                <SearchInput
                  onSearch={setSearchQuery}
                  placeholder="Search by LRN, Reference No., Invoice..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">Marketplace</Label>
                <Select value={selectedMarketplace} onValueChange={setSelectedMarketplace}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Marketplaces" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Marketplaces</SelectItem>
                    {marketplaces.map((mp) => (
                      <SelectItem key={mp.id} value={mp.id}>{mp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {isActiveTab && (
                <div>
                  <Label className="text-sm">Status</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="CREATED">CREATED</SelectItem>
                      <SelectItem value="MANIFESTED">MANIFESTED</SelectItem>
                      <SelectItem value="PICKED_UP">PICKED_UP</SelectItem>
                      <SelectItem value="LEFT_ORIGIN">LEFT_ORIGIN</SelectItem>
                      <SelectItem value="REACH_DESTINATION">REACH_DESTINATION</SelectItem>
                      <SelectItem value="UNDEL_REATTEMPT">UNDEL_REATTEMPT</SelectItem>
                      <SelectItem value="PART_DEL">PART_DEL</SelectItem>
                      <SelectItem value="OFD">OFD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-sm">From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-end">
                <Button variant="outline" onClick={handleClearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading...</div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center text-slate-600">No appointments found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LRN</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference No.</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Marketplace</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice No.</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Appointment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        {!isActiveTab && (
                          <>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">GRN Status</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Dispatch Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Accepted Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rejected Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Collected</th>
                          </>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle text-sm">
                            <span className="font-medium text-teal-600">{order.lrn || 'N/A'}</span>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <button
                              onClick={() => navigate(`/seller/orders/${order.id}`)}
                              className="text-blue-600 hover:underline"
                            >
                              {order.reference_number || '-'}
                            </button>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">{order.b2b_marketplace_name || 'N/A'}</td>
                          <td className="px-4 py-3 align-middle text-sm">{order.invoice_number}</td>
                          <td className="px-4 py-3 align-middle text-sm font-medium text-right">₹{order.invoice_amount?.toLocaleString()}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            {order.appointment_date ? (
                              <div className="whitespace-nowrap">
                                <div>{order.appointment_date}</div>
                                {order.appointment_time_range && (
                                  <div className="text-xs text-slate-500">{order.appointment_time_range}</div>
                                )}
                              </div>
                            ) : order.appointment_datetime ? (
                              <div className="whitespace-nowrap">
                                <div>{formatToIST(order.appointment_datetime, false)}</div>
                                <div className="text-xs text-slate-500">{formatToIST(order.appointment_datetime).split(', ')[1]}</div>
                              </div>
                            ) : (
                              'N/A'
                            )}
                            {isDelhiveryAppointmentBooked(order) && (
                              <div className="mt-1">
                                <Badge className="bg-green-100 text-green-800 text-xs" title={order.delhivery_appointment_id ? `Booked with Delhivery — Appointment ID: ${order.delhivery_appointment_id}` : "Booked with Delhivery"}>
                                  Booked ✓
                                </Badge>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="whitespace-nowrap">
                              <div>{formatToIST(order.created_at, false)}</div>
                              <div className="text-xs text-slate-500">{formatToIST(order.created_at).split(', ')[1]}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <Badge className={STATUS_COLORS[order.status] || "bg-gray-100 text-gray-800"}>
                              {order.status}
                            </Badge>
                          </td>
                          {!isActiveTab && (
                            <>
                              <td className="px-4 py-3 align-middle text-sm">
                                <Badge className={GRN_STATUS_COLORS[order.grn_status] || "bg-gray-100 text-gray-800"}>
                                  {order.grn_status}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_dispatch_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_accepted_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{order.total_rejected_quantity || 0}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">
                                <button
                                  onClick={() => handleViewPayments(order)}
                                  className="text-blue-600 hover:underline"
                                >
                                  ₹{order.amount_collected?.toLocaleString() || 0}
                                </button>
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex gap-2">
                              {!isActiveTab && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenGRNDialog(order)}
                                    title="Update GRN"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenPaymentDialog(order)}
                                    title="Record Payment"
                                  >
                                    <Wallet className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    Showing {orders.length} of {totalOrders} appointments
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* GRN Dialog */}
      <Dialog open={showGRNDialog} onOpenChange={setShowGRNDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update GRN Status</DialogTitle>
            <DialogDescription>
              Update Goods Receipt Note for Order: {selectedOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>GRN Status</Label>
              <Select value={grnStatus} onValueChange={setGrnStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRN_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Total Dispatch Quantity</Label>
              <Input
                type="number"
                value={dispatchQty}
                onChange={(e) => setDispatchQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Total Accepted Quantity</Label>
              <Input
                type="number"
                value={acceptedQty}
                onChange={(e) => setAcceptedQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Total Rejected Quantity</Label>
              <Input
                type="number"
                value={rejectedQty}
                onChange={(e) => setRejectedQty(e.target.value)}
                className="mt-1"
                min="0"
              />
            </div>
            
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={grnRemarks}
                onChange={(e) => setGrnRemarks(e.target.value)}
                className="mt-1"
                placeholder="Add any notes or remarks..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGRNDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveGRN}>Save GRN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record payment collection for Order: {selectedOrder?.order_number}
              <br />
              <span className="font-semibold">Invoice Amount: ₹{selectedOrder?.invoice_amount?.toLocaleString()}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Amount Collected (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-1"
                placeholder="Enter amount"
                min="0"
              />
            </div>
            
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Transaction Reference (Optional)</Label>
              <Input
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                className="mt-1"
                placeholder="Transaction ID, Cheque No., etc."
              />
            </div>
            
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                className="mt-1"
                placeholder="Add any notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payments List Dialog */}
      <Dialog open={showPaymentsListDialog} onOpenChange={setShowPaymentsListDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
            <DialogDescription>
              Order: {selectedOrder?.order_number} | Invoice: ₹{selectedOrder?.invoice_amount?.toLocaleString()}
              <br />
              <span className="text-green-600 font-semibold">Total Collected: ₹{paymentsTotal?.toLocaleString()}</span>
              {" | "}
              <span className={paymentsRemaining > 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                Remaining: ₹{paymentsRemaining?.toLocaleString()}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {paymentsList.length === 0 ? (
              <p className="text-center text-slate-600 py-8">No payments recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Method</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {paymentsList.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-4 py-3 align-middle text-sm">
                          <div className="whitespace-nowrap">
                            <div>{formatToIST(payment.payment_date, false)}</div>
                            <div className="text-xs text-slate-500">{formatToIST(payment.payment_date).split(', ')[1]}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.payment_method}</td>
                        <td className="px-4 py-3 align-middle text-sm font-medium text-green-600 text-right">₹{payment.amount_collected?.toLocaleString()}</td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.transaction_reference || '-'}</td>
                        <td className="px-4 py-3 align-middle text-sm">{payment.remarks || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPaymentsListDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default SellerAppointments;
